import type { HeaderLabel } from './columnSpec.types';

export type HeaderLabelContext = {
  t: (key: string, options?: Record<string, unknown>) => string;
  /** tenant-specific name for PLB; `claYear.plbName` */
  plbName?: string;
  /** the year in the selector; undefined before the CLA year has loaded */
  selectedYear?: number;
};

const NAMESPACE = 'employmentHours';

/**
 * Resolves a column or group header to a display string.
 *
 * Replaces three Dojo mechanisms:
 * - `label.replace('PLB', plbName)` at ~30 sites -> i18next `{{plbName}}` interpolation, which is
 *   translator-safe (the Dutch and English strings need not contain the literal "PLB").
 * - `selectedYear ? selectedYear - n : nls.<fallback>` -> `{ kind: 'year', offset }`.
 * - `'' + selectedYear - 6 + ' ' + andOlder` -> `{ andOlder: true }`. That Dojo expression only
 *   worked through accidental string/number coercion; do not "restore" it.
 */
export const resolveHeaderLabel = (
  label: HeaderLabel,
  { t, plbName, selectedYear }: HeaderLabelContext
): string => {
  switch (label.kind) {
    case 'text':
      return t(`${NAMESPACE}.${label.key}`);

    case 'plb':
      return t(`${NAMESPACE}.${label.key}`, { plbName: plbName ?? '' });

    case 'year': {
      if (selectedYear === undefined) {
        return label.fallbackIsPlb
          ? t(`${NAMESPACE}.${label.fallbackKey}`, { plbName: plbName ?? '' })
          : t(`${NAMESPACE}.${label.fallbackKey}`);
      }

      const value = selectedYear - label.offset;

      return label.andOlder
        ? t(`${NAMESPACE}.yearAndOlder`, { year: value })
        : String(value);
    }
  }
};

/**
 * Header tooltip key, honouring the plus/minus presentation mode.
 *
 * The Dojo translations define `hoursBalanceTooltipPlusMinus` and
 * `hoursBalanceCumRealTooltipPlusMinus` (`nls/aysist.js:1290-1293`) but never used them
 * (`GridColumns.js:325,334` always pass the plain variant) — fixed here.
 */
const HAS_PLUS_MINUS_TOOLTIP = ['hoursBalance', 'hoursBalanceCumReal'];

export const resolveTooltipKey = (
  tooltipKey: string,
  showPlusMinus: boolean
): string =>
  showPlusMinus && HAS_PLUS_MINUS_TOOLTIP.includes(tooltipKey)
    ? `${tooltipKey}PlusMinus`
    : tooltipKey;

export const resolveTooltip = (
  tooltipKey: string,
  { t, plbName }: Pick<HeaderLabelContext, 't' | 'plbName'>,
  showPlusMinus: boolean
): string =>
  t(`${NAMESPACE}.tooltips.${resolveTooltipKey(tooltipKey, showPlusMinus)}`, {
    plbName: plbName ?? ''
  });
